import { Controller } from "@hotwired/stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import { Modal } from "bootstrap"

export default class extends Controller {
  static values = {
    eventsUrl: String,
    centerId: String,
    startDate: String,
    createNoteUrl: String
  }

  static targets = [
    "calendar",
    "noteModal",
    "noteContent",
    "noteDateDisplay",
    "noteTimeDisplay",
    "noteErrors",
    "noteHour",
    "noteMinute",
    "noteEndHour",
    "noteEndMinute",
    "noteSubmitButton",
    "noteSubmitSpinner",
    "flash",
    "flashMessage",
    "noteViewModal",
    "noteViewTimeDisplay",
    "noteViewContent",
    "noteViewCreator",
    "noteViewDeleteButton"
  ]

  connect() {
    this.selectedSlot = null
    this.selectedNoteEvent = null
    this.noteModalInstance = this.hasNoteModalTarget ? new Modal(this.noteModalTarget) : null
    this.noteViewModalInstance = this.hasNoteViewModalTarget ? new Modal(this.noteViewModalTarget) : null

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],

      // View configuration
      initialView: "timeGridWeek",
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "timeGridWeek,timeGridDay"
      },

      // Time configuration
      slotDuration: "00:30:00", // 30-minute slots
      slotMinTime: "06:00:00",  // Start at 6 AM
      slotMaxTime: "23:00:00",  // End at 11 PM
      allDaySlot: false,        // Hide all-day slot

      // Locale and timezone
      locale: "vi",
      timeZone: "Asia/Ho_Chi_Minh",
      firstDay: 1, // Monday

      // Initial date
      initialDate: this.startDateValue || new Date(),

      // Event source
      events: this.eventsUrlValue,

      // Display settings
      height: "auto",
      expandRows: true,
      nowIndicator: true,

      // Event rendering
      eventTimeFormat: {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      },

      slotLabelFormat: {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      },

      // Event click handler
      eventClick: this.handleEventClick.bind(this),

      // Date click handler
      dateClick: this.handleDateClick.bind(this),

      // Event rendering callback for custom styling
      eventDidMount: this.handleEventMount.bind(this)
    })

    this.calendar.render()
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy()
    }
  }

  handleEventClick(info) {
    const event = info.event
    const courseId = event.extendedProps.courseId
    const isScheduleNote = event.extendedProps.isScheduleNote

    if (isScheduleNote) {
      this.handleScheduleNoteClick(event)
      return
    }

    // Show course details modal or navigate to course page
    if (courseId) {
      window.location.href = `/quanly/courses/${courseId}`
    }
  }

  handleDateClick(info) {
    if (!this.ensureCenterSelected()) {
      window.alert("Vui lòng chọn một cơ sở cụ thể trước khi thêm ghi chú.")
      return
    }

    const slot = this.buildSlotFromIsoString(info.dateStr)
    this.openNoteModalForSlot(slot)
  }

  handleEventMount(info) {
    const event = info.event

    // Add custom classes based on attendance
    if (event.extendedProps.isLowAttendance) {
      info.el.classList.add('fc-event-low-attendance')
    }

    if (event.extendedProps.isFullAttendance) {
      info.el.classList.add('fc-event-full-attendance')
    }

    // Add tooltip with more details
    info.el.title = event.extendedProps.tooltip || event.title
  }

  // Refresh events when filters change
  refresh() {
    if (this.calendar) {
      this.calendar.refetchEvents()
    }
  }

  async submitScheduleNote() {
    if (!this.selectedSlot) {
      this.showErrors("Không xác định được khung giờ. Vui lòng chọn lại trên lịch.")
      return
    }

    const centerId = this.centerIdValue
    if (!centerId) {
      this.showErrors("Vui lòng chọn cơ sở trước khi lưu ghi chú.")
      return
    }

    const content = this.noteContentTarget.value.trim()
    if (!content) {
      this.showErrors("Nội dung ghi chú không được để trống.")
      this.noteContentTarget.focus()
      return
    }

    this.setSubmittingState(true)

    try {
      const payload = {
        schedule_note: {
          date: this.selectedSlot.date,
          hour: this.selectedSlot.hour,
          minute: this.selectedSlot.minute,
          end_hour: this.selectedSlot.endHour,
          end_minute: this.selectedSlot.endMinute,
          content,
          center_id: centerId
        }
      }

      const response = await fetch(this.createNoteUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        this.handleSuccessfulSave()
      } else {
        const data = await response.json().catch(() => ({}))
        const errors = Array.isArray(data.errors) ? data.errors.join(", ") : "Không thể lưu ghi chú. Vui lòng thử lại."
        this.showErrors(errors)
      }
    } catch (error) {
      this.showErrors("Có lỗi kết nối. Vui lòng thử lại.")
    } finally {
      this.setSubmittingState(false)
    }
  }

  handleSuccessfulSave() {
    this.selectedSlot = null
    if (this.noteModalInstance) {
      this.noteModalInstance.hide()
    }
    this.showFlashMessage("Đã thêm ghi chú vào lịch.")
    this.refresh()
  }

  hideFlash() {
    if (this.hasFlashTarget) {
      this.flashTarget.classList.add("d-none")
      this.flashMessageTarget.textContent = ""
    }
  }

  handleTimeChange() {
    if (!this.selectedSlot) return

    const hour = this.hasNoteHourTarget ? parseInt(this.noteHourTarget.value, 10) : this.selectedSlot.hour
    const minute = this.hasNoteMinuteTarget ? parseInt(this.noteMinuteTarget.value, 10) : this.selectedSlot.minute

    if (Number.isNaN(hour) || Number.isNaN(minute)) return

    this.selectedSlot.hour = hour
    this.selectedSlot.minute = minute
    this.ensureEndAfterStart()
    this.updateTimeDisplay()
  }

  handleStartTimeChange() {
    this.handleTimeChange()
  }

  handleEndTimeChange() {
    if (!this.selectedSlot) return
    if (!this.hasNoteEndHourTarget || !this.hasNoteEndMinuteTarget) return

    const hour = parseInt(this.noteEndHourTarget.value, 10)
    const minute = parseInt(this.noteEndMinuteTarget.value, 10)

    if (Number.isNaN(hour) || Number.isNaN(minute)) return

    const startTotal = this.selectedSlot.hour * 60 + this.selectedSlot.minute
    const endTotal = hour * 60 + minute

    if (endTotal <= startTotal) {
      window.alert("Giờ kết thúc phải sau giờ bắt đầu (tối thiểu 30 phút).")
      this.syncEndTimeInputs()
      return
    }

    this.selectedSlot.endHour = hour
    this.selectedSlot.endMinute = minute
    this.updateTimeDisplay()
  }

  ensureEndAfterStart() {
    if (!this.selectedSlot) return

    const startTotal = this.selectedSlot.hour * 60 + this.selectedSlot.minute
    const endTotal = this.selectedSlot.endHour * 60 + this.selectedSlot.endMinute

    if (endTotal <= startTotal) {
      const adjusted = this.calculateDefaultEnd(this.selectedSlot.hour, this.selectedSlot.minute)
      this.selectedSlot.endHour = adjusted.hour
      this.selectedSlot.endMinute = adjusted.minute
      this.syncEndTimeInputs()
    }
  }

  updateTimeDisplay() {
    if (!this.selectedSlot) return
    this.selectedSlot.displayTime = this.formatTimeRange(
      this.selectedSlot.hour,
      this.selectedSlot.minute,
      this.selectedSlot.endHour,
      this.selectedSlot.endMinute
    )
    if (this.hasNoteTimeDisplayTarget) {
      this.noteTimeDisplayTarget.textContent = this.selectedSlot.displayTime
    }
  }

  ensureCenterSelected() {
    return Boolean(this.centerIdValue)
  }

  showErrors(message) {
    if (this.hasNoteErrorsTarget) {
      this.noteErrorsTarget.classList.remove("d-none")
      this.noteErrorsTarget.textContent = message
    } else {
      window.alert(message)
    }
  }

  hideErrors() {
    if (this.hasNoteErrorsTarget) {
      this.noteErrorsTarget.classList.add("d-none")
      this.noteErrorsTarget.textContent = ""
    }
  }

  setSubmittingState(isSubmitting) {
    if (this.hasNoteSubmitButtonTarget) {
      this.noteSubmitButtonTarget.disabled = isSubmitting
    }
    if (this.hasNoteSubmitSpinnerTarget) {
      this.noteSubmitSpinnerTarget.classList.toggle("d-none", !isSubmitting)
    }
  }

  showFlashMessage(message) {
    if (!this.hasFlashTarget || !this.hasFlashMessageTarget) return

    this.flashMessageTarget.textContent = message
    this.flashTarget.classList.remove("d-none")

    clearTimeout(this.flashTimeout)
    this.flashTimeout = setTimeout(() => {
      this.hideFlash()
    }, 4000)
  }

  handleScheduleNoteClick(event) {
    this.selectedNoteEvent = event
    const noteProps = event?.extendedProps || {}
    this.showNoteViewModal(noteProps)
  }

  async deleteScheduleNote(url) {
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.csrfToken()
        }
      })

      if (response.ok) {
        this.showFlashMessage("Đã xóa ghi chú khỏi lịch.")
        this.refresh()
      } else {
        const data = await response.json().catch(() => ({}))
        const errors = Array.isArray(data.errors) ? data.errors.join(", ") : "Không thể xóa ghi chú. Vui lòng thử lại."
        window.alert(errors)
      }
    } catch (error) {
      window.alert("Có lỗi xảy ra khi xóa ghi chú. Vui lòng thử lại.")
    }
  }

  buildSlotFromIsoString(isoString) {
    const datePart = isoString.split("T")[0]
    const { hour, minute } = this.extractHourMinute(isoString)
    const defaultEnd = this.calculateDefaultEnd(hour, minute)

    return {
      date: datePart,
      hour,
      minute,
      endHour: defaultEnd.hour,
      endMinute: defaultEnd.minute,
      displayDate: this.formatDateForDisplay(isoString),
      displayTime: this.formatTimeRange(hour, minute, defaultEnd.hour, defaultEnd.minute)
    }
  }

  buildSlotFromNoteProps(noteProps, event = null) {
    if (noteProps.startIso && noteProps.endIso) {
      return this.buildSlotFromIsoRange(noteProps.startIso, noteProps.endIso)
    }

    if (noteProps.date && noteProps.hour !== undefined && noteProps.minute !== undefined) {
      const hour = parseInt(noteProps.hour, 10)
      const minute = parseInt(noteProps.minute, 10)
      const endHour = parseInt(noteProps.endHour ?? noteProps.end_hour ?? hour, 10)
      const endMinute = parseInt(noteProps.endMinute ?? noteProps.end_minute ?? minute, 10)

      return {
        date: noteProps.date,
        hour,
        minute,
        endHour,
        endMinute,
        displayDate: noteProps.dateDisplay || this.formatDateForDisplay(`${noteProps.date}T${this.padNumber(hour)}:${this.padNumber(minute)}:00`),
        displayTime: this.formatTimeRange(hour, minute, endHour, endMinute)
      }
    }

    if (event?.start) {
      const startIso = event.start.toISOString()
      const endIso = (event.end || new Date(event.start.getTime() + 30 * 60 * 1000)).toISOString()
      return this.buildSlotFromIsoRange(startIso, endIso)
    }

    return null
  }

  buildSlotFromIsoRange(startIso, endIso) {
    const { hour, minute } = this.extractHourMinute(startIso)
    const { hour: endHour, minute: endMinute } = this.extractHourMinute(endIso)

    return {
      date: startIso.split("T")[0],
      hour,
      minute,
      endHour,
      endMinute,
      displayDate: this.formatDateForDisplay(startIso),
      displayTime: this.formatTimeRange(hour, minute, endHour, endMinute)
    }
  }

  syncTimeInputs() {
    if (!this.selectedSlot) return

    if (this.hasNoteHourTarget) {
      this.noteHourTarget.value = this.selectedSlot.hour
    }
    if (this.hasNoteMinuteTarget) {
      this.noteMinuteTarget.value = this.selectedSlot.minute
    }
    this.syncEndTimeInputs()
  }

  syncEndTimeInputs() {
    if (!this.selectedSlot) return

    if (this.hasNoteEndHourTarget) {
      this.noteEndHourTarget.value = this.selectedSlot.endHour
    }
    if (this.hasNoteEndMinuteTarget) {
      this.noteEndMinuteTarget.value = this.selectedSlot.endMinute
    }
  }

  openNoteModalForSlot(slot) {
    this.selectedSlot = slot

    if (!this.selectedSlot) return

    this.hideErrors()

    if (this.hasNoteContentTarget) {
      this.noteContentTarget.value = ""
    }

    if (this.hasNoteDateDisplayTarget) {
      this.noteDateDisplayTarget.textContent = this.selectedSlot.displayDate
    }
    if (this.hasNoteTimeDisplayTarget) {
      this.noteTimeDisplayTarget.textContent = this.selectedSlot.displayTime
    }

    this.syncTimeInputs()

    if (this.noteModalInstance) {
      this.noteModalInstance.show()
    }
  }

  showNoteViewModal(noteProps) {
    if (!this.noteViewModalInstance) {
      return
    }

    if (this.hasNoteViewTimeDisplayTarget) {
      this.noteViewTimeDisplayTarget.textContent = noteProps.timeRange || "Khung giờ không xác định"
    }

    if (this.hasNoteViewContentTarget) {
      this.noteViewContentTarget.textContent = noteProps.content || "(Không có nội dung)"
    }

    if (this.hasNoteViewCreatorTarget) {
      this.noteViewCreatorTarget.textContent = noteProps.createdBy ? `Người tạo: ${noteProps.createdBy}` : ""
    }

    if (this.hasNoteViewDeleteButtonTarget) {
      this.noteViewDeleteButtonTarget.disabled = !noteProps.destroyUrl
      this.noteViewDeleteButtonTarget.dataset.destroyUrl = noteProps.destroyUrl || ""
    }

    this.noteViewModalInstance.show()
  }

  openAddNoteFromCurrent() {
    if (!this.selectedNoteEvent) return
    const slot = this.buildSlotFromNoteProps(this.selectedNoteEvent.extendedProps, this.selectedNoteEvent)

    if (!slot) {
      this.showErrors("Không xác định được khung giờ. Vui lòng chọn lại trên lịch.")
      return
    }

    if (this.noteViewModalInstance) {
      this.noteViewModalInstance.hide()
    }

    this.selectedNoteEvent = null
    this.openNoteModalForSlot(slot)
  }

  deleteCurrentNote() {
    if (!this.hasNoteViewDeleteButtonTarget) return

    const url = this.noteViewDeleteButtonTarget.dataset.destroyUrl
    if (!url) {
      this.showErrors("Không tìm thấy liên kết xóa ghi chú.")
      return
    }

    const confirmed = window.confirm("Bạn có chắc muốn xóa ghi chú này?")
    if (!confirmed) return

    if (this.noteViewModalInstance) {
      this.noteViewModalInstance.hide()
    }

    this.selectedNoteEvent = null
    this.deleteScheduleNote(url)
  }

  extractHourMinute(isoString) {
    const timeZone = this.calendar?.getOption("timeZone") || "UTC"
    const formatter = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone
    })
    const parts = formatter.formatToParts(new Date(isoString))
    const hour = parseInt(parts.find((part) => part.type === "hour").value, 10)
    const minute = parseInt(parts.find((part) => part.type === "minute").value, 10)

    return { hour, minute }
  }

  formatDateForDisplay(isoString) {
    const timeZone = this.calendar?.getOption("timeZone") || "UTC"
    return new Intl.DateTimeFormat("vi-VN", {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone
    }).format(new Date(isoString))
  }

  formatTimeRange(startHour, startMinute, endHour, endMinute) {
    return `Khung giờ: ${this.formatSingleTime(startHour, startMinute)} - ${this.formatSingleTime(endHour, endMinute)}`
  }

  formatSingleTime(hour, minute) {
    return `${this.padNumber(hour)}h${this.padNumber(minute)}`
  }

  padNumber(number) {
    return number.toString().padStart(2, "0")
  }

  calculateDefaultEnd(startHour, startMinute) {
    const totalMinutes = startHour * 60 + startMinute + 30
    let hour = Math.floor(totalMinutes / 60)
    let minute = totalMinutes % 60

    if (hour > 23) {
      hour = 23
      minute = 30
    }

    return { hour, minute }
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }
}
